'use client';

/**
 * Cart Modal - Exactly like Vercel Commerce
 * Uses @headlessui/react for Dialog transitions
 */

import { Dialog, Transition } from '@headlessui/react';
import { ShoppingCartIcon } from '@heroicons/react/24/outline';
import Image from 'next/image';
import Link from 'next/link';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useCurrency } from '@/themes/core/hooks/useCurrency';
import supabaseLoader from '@/lib/supabase-image-loader';

import { useCart, useThemePath, useWishlist } from '@/themes/core/hooks';
import type { CartItem } from '@/themes/core/hooks/useCart';
import Price from '../ui/Price';
import OpenCart from './OpenCart';
import CloseCart from './CloseCart';
import { EditItemQuantityButton } from './EditItemQuantityButton';
import { DeleteItemButton } from './DeleteItemButton';
import { getValidImageUrl } from '@/lib/utils';
import { Heart, Trash2 } from 'lucide-react';
import { useSiteContext } from '@/app/tenant/TenantLayoutWrapper';
import { useEcommerce } from '@/hooks/useEcommerce';

export default function CartModal({ variant = 'default' }: { variant?: 'default' | 'minimal' }) {
    const { items, count, total, currency, removeFromCart, updateQuantity, isOpen: isCartOpen, setIsOpen: setCartOpen, addToCart } = useCart();
    const {
        items: wishlistItems,
        removeItem: removeFromWishlist,
        clear: clearWishlist,
        isOpen: isWishlistOpen,
        closeWishlist,
        openWishlist
    } = useWishlist();

    const { productLink, checkoutLink, collectionLink } = useThemePath();
    const { siteId } = useSiteContext();
    const ecommerce = useEcommerce(siteId || '', currency || 'DZD');
    const { formatBasePrice } = useCurrency();

    // Unified Open State
    const isOpen = isCartOpen || isWishlistOpen;

    // View State (Cart vs Wishlist)
    const [view, setView] = useState<'cart' | 'wishlist'>('cart');
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Sync view with open triggers
    // Sync view with open triggers and track events
    useEffect(() => {
        if (isWishlistOpen) {
            setView('wishlist');
        } else if (isCartOpen) {
            setView('cart');
            // Track View Cart
            ecommerce.viewCart(
                items.map(item => ({
                    item_id: item.id,
                    item_name: item.name,
                    price: item.price,
                    quantity: item.quantity,
                    item_variant: item.variant?.name
                })),
                total,
                currency
            );
        }
    }, [isCartOpen, isWishlistOpen, ecommerce, currency, total, items]);

    const closeDrawer = () => {
        setCartOpen(false);
        closeWishlist();
        // Reset view after animation
        setTimeout(() => setView('cart'), 300);
    };

    // Auto-open cart when items added (only if not already open as wishlist)
    const quantityRef = useRef(count);
    useEffect(() => {
        if (count && count !== quantityRef.current && count > 0) {
            if (!isOpen) {
                setCartOpen(true);
            }
            quantityRef.current = count;
        }
    }, [count, isOpen, setCartOpen]);



    // Handle "Add All to Cart"
    const handleAddAllToCart = () => {
        wishlistItems.forEach(item => {
            addToCart(
                {
                    id: item.id,
                    name: item.name,
                    price: item.price,
                    image: item.image, // Fixed: removed array wrap as interface expects string
                    slug: item.slug,
                    variant: item.variantId ? { id: item.variantId, name: '' } : undefined // Partial mapping
                } as any, // Type adaptation
                undefined,
                1
            );
        });
        clearWishlist();
        closeWishlist();
        setCartOpen(true); // Switch to cart view
    };

    const wishlistCount = wishlistItems.length;

    return (
        <>
            <button aria-label="Open cart" onClick={() => setCartOpen(true)}>
                <OpenCart variant={variant} quantity={isMounted ? count : 0} />
            </button>
            <Transition show={isOpen}>
                <Dialog onClose={closeDrawer} className="relative z-50">
                    {/* Backdrop */}
                    <Transition.Child
                        as={Fragment}
                        enter="transition-all ease-in-out duration-300"
                        enterFrom="opacity-0 backdrop-blur-none"
                        enterTo="opacity-100 backdrop-blur-[.5px]"
                        leave="transition-all ease-in-out duration-200"
                        leaveFrom="opacity-100 backdrop-blur-[.5px]"
                        leaveTo="opacity-0 backdrop-blur-none"
                    >
                        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    </Transition.Child>

                    {/* Panel */}
                    <Transition.Child
                        as={Fragment}
                        enter="transition-all ease-in-out duration-300"
                        enterFrom="translate-x-[-100%]"
                        enterTo="translate-x-0"
                        leave="transition-all ease-in-out duration-200"
                        leaveFrom="translate-x-0"
                        leaveTo="translate-x-[-100%]"
                    >
                        <Dialog.Panel className="fixed bottom-0 left-0 top-0 flex h-full w-full flex-col border-r border-border bg-background text-foreground backdrop-blur-xl md:w-[390px]">

                            {/* Header Tabs */}
                            <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
                                <div className="flex bg-neutral-100 dark:bg-neutral-800 p-1 rounded-lg">
                                    <button
                                        onClick={() => {
                                            closeWishlist();
                                            setCartOpen(true);
                                        }}
                                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${view === 'cart'
                                            ? 'bg-white text-primary shadow-sm dark:bg-neutral-900 dark:text-primary'
                                            : 'text-neutral-500 hover:text-primary dark:text-neutral-400'
                                            }`}
                                    >
                                        السلة ({count})
                                    </button>
                                    <button
                                        onClick={() => {
                                            setCartOpen(false);
                                            openWishlist();
                                        }}
                                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${view === 'wishlist'
                                            ? 'bg-secondary text-secondary-foreground shadow-sm'
                                            : 'text-neutral-500 hover:text-secondary dark:text-neutral-400'
                                            }`}
                                    >
                                        <Heart className={`h-3.5 w-3.5 ${view === 'wishlist' ? 'fill-current' : ''}`} />
                                        المفضلة ({wishlistCount})
                                    </button>
                                </div>

                                <button aria-label="إغلاق" onClick={closeDrawer} className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-full transition-colors">
                                    <CloseCart />
                                </button>
                            </div>

                            {/* Content Area */}
                            <div className="flex-1 overflow-y-auto p-4">
                                {view === 'cart' ? (
                                    // --- CART CONTENT ---
                                    items.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center h-full text-center mt-10">
                                            <ShoppingCartIcon className="h-16 w-16 text-neutral-300 mb-4" />
                                            <p className="text-xl font-bold mb-2">سلتك فارغة</p>
                                            <Link
                                                href={collectionLink()}
                                                onClick={closeDrawer}
                                                className="mt-4 rounded-md bg-primary px-6 py-3 text-primary-foreground hover:opacity-90"
                                            >
                                                تصفح المنتجات
                                            </Link>
                                        </div>
                                    ) : (
                                        <ul className="space-y-4">
                                            {items.map((item: CartItem, i: number) => (
                                                <li key={`${item.productId}-${i}`} className="flex w-full flex-col border-b border-border pb-4">
                                                    <div className="relative flex w-full flex-row justify-between">
                                                        <div className="absolute z-40 left-0 top-0">
                                                            <DeleteItemButton onClick={() => removeFromCart(item.id)} />
                                                        </div>
                                                        <div className="flex flex-row gap-4">
                                                            <div className="relative h-20 w-20 overflow-hidden rounded-md border border-border bg-muted">
                                                                <Image
                                                                    loader={supabaseLoader}
                                                                    className="h-full w-full object-cover"
                                                                    width={80}
                                                                    height={80}
                                                                    alt={item.name}
                                                                    src={getValidImageUrl(item.image)}
                                                                />
                                                            </div>
                                                            <div className="flex flex-col justify-between">
                                                                <Link href={productLink(item.slug)} onClick={closeDrawer} className="font-medium text-base hover:underline line-clamp-2">
                                                                    {item.name}
                                                                </Link>
                                                                {item.variant && (
                                                                    <p className="text-sm text-neutral-500">
                                                                        {item.variant.options
                                                                            ? Object.entries(item.variant.options).map(([k, v]) => `${k}: ${v}`).join(' / ')
                                                                            : item.variant.name}
                                                                    </p>
                                                                )}
                                                                <div className="flex items-center mt-2 border border-neutral-200 dark:border-neutral-700 rounded-full w-fit">
                                                                    <EditItemQuantityButton type="minus" onClick={() => updateQuantity(item.id, Math.max(0, item.quantity - 1))} />
                                                                    <span className="w-8 text-center text-sm">{item.quantity}</span>
                                                                    <EditItemQuantityButton type="plus" onClick={() => updateQuantity(item.id, item.quantity + 1)} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col justify-end text-left">
                                                            <Price
                                                                className="text-sm font-medium"
                                                                amount={item.price * item.quantity}
                                                            />
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )
                                ) : (
                                    // --- WISHLIST CONTENT ---
                                    wishlistItems.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center h-full text-center mt-10">
                                            <Heart className="h-16 w-16 text-neutral-300 mb-4" />
                                            <p className="text-xl font-bold mb-2">قائمة المفضلة فارغة</p>
                                            <Link
                                                href={collectionLink()}
                                                onClick={closeDrawer}
                                                className="mt-4 rounded-md bg-primary px-6 py-3 text-primary-foreground hover:opacity-90"
                                            >
                                                تصفح المنتجات
                                            </Link>
                                        </div>
                                    ) : (
                                        <ul className="space-y-4">
                                            {wishlistItems.map((item) => (
                                                <li key={item.id} className="flex gap-4 py-4 border-b border-neutral-100 dark:border-neutral-800">
                                                    <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-neutral-100 relative">
                                                        <Image
                                                            loader={supabaseLoader}
                                                            src={getValidImageUrl(item.image)}
                                                            alt={item.name}
                                                            className="h-full w-full object-cover"
                                                            width={80}
                                                            height={80}
                                                        />
                                                    </div>
                                                    <div className="flex flex-1 flex-col justify-center">
                                                        <div className="flex justify-between items-start">
                                                            <Link href={productLink(item.slug)} onClick={closeDrawer} className="text-sm font-medium hover:underline line-clamp-2">
                                                                {item.name}
                                                            </Link>
                                                            <button onClick={() => removeFromWishlist(item.id)} className="text-neutral-400 hover:text-red-500 transition-colors">
                                                                <Trash2 className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                        <div className="mt-2 flex items-center justify-between">
                                                            <p className="text-sm font-bold">
                                                                {formatBasePrice(item.price)}
                                                            </p>
                                                            <button onClick={() => {
                                                                addToCart({ id: item.id, name: item.name, price: item.price, images: [item.image], slug: item.slug } as any, undefined, 1);
                                                                removeFromWishlist(item.id);
                                                                setCartOpen(true); // Switch to cart view implicitly
                                                            }} className="text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity">
                                                                أضف للسلة
                                                            </button>
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )
                                )}
                            </div>

                            {/* Footer */}
                            {view === 'cart' && items.length > 0 && (
                                <div className="border-t border-border p-4 space-y-4 bg-background">
                                    <div className="flex justify-between text-base font-medium">
                                        <span>المجموع</span>
                                        <Price amount={total} />
                                    </div>
                                    <Link
                                        href={checkoutLink()}
                                        onClick={closeDrawer}
                                        className="block w-full rounded-full bg-primary p-3 text-center text-sm font-medium text-primary-foreground hover:opacity-90"
                                    >
                                        متابعة الدفع
                                    </Link>
                                </div>
                            )}

                            {view === 'wishlist' && wishlistItems.length > 0 && (
                                <div className="border-t border-border p-4 space-y-4 bg-background">
                                    <button
                                        onClick={handleAddAllToCart}
                                        className="block w-full rounded-full bg-primary p-3 text-center text-sm font-medium text-primary-foreground hover:opacity-90"
                                    >
                                        نقل الكل لسلة التسوق
                                    </button>
                                </div>
                            )}
                        </Dialog.Panel>
                    </Transition.Child>
                </Dialog>
            </Transition>
        </>
    );
}
